import { useMemo, useRef } from 'react';
import type { Level, SeatDto } from '@okn/shared';
import type { SeatmapByLevel } from '../api/useSeatmap.ts';
import { Legend } from './Legend.tsx';
import { PanZoom, type Bounds, type PanZoomHandle } from './PanZoom.tsx';
import type { SeatStateContext } from './seat-state.ts';
import { SeatLayer } from './SeatLayer.tsx';

type Props = {
  data: SeatmapByLevel;
  selectedIds: Set<string>;
  stateContext?: SeatStateContext;
  onSelect?: (seat: SeatDto) => void;
};

// Visual layout for the overview "category" mode. Overview card dimensions are
// independent of the underlying seat geometry — so a 36-seat balcony reads at
// a comparable size to a 837-seat ground floor. The actual seat positions are
// used in detail mode (and to compute the zoom-to target).
const SECTION_LAYOUT: Record<
  Level,
  {
    x: number;
    y: number;
    title: string;
    subtitle: string;
    overviewW: number;
    overviewH: number;
  }
> = {
  ground: {
    x: 0,
    y: 0,
    title: 'Alt Kat',
    subtitle: 'Ana Salon',
    overviewW: 900,
    overviewH: 460,
  },
  side_balcony_left: {
    x: -640,
    y: -160,
    title: 'Sol Balkon',
    subtitle: 'Orta Kat',
    overviewW: 300,
    overviewH: 420,
  },
  side_balcony_right: {
    x: 640,
    y: -160,
    title: 'Sağ Balkon',
    subtitle: 'Orta Kat',
    overviewW: 300,
    overviewH: 420,
  },
  upper_balcony: {
    x: 0,
    y: -500,
    title: 'Üst Balkon',
    subtitle: 'Üst Kat',
    overviewW: 760,
    overviewH: 250,
  },
};

const LEVEL_ORDER: Level[] = [
  'ground',
  'side_balcony_left',
  'side_balcony_right',
  'upper_balcony',
];

const OVERVIEW_FULL_AT = 1.05;
const OVERVIEW_HIDE_AT = 1.7;
const SEATS_HIDDEN_BELOW = 1.15;
const SEATS_FULL_AT = 1.85;

function lerp01(v: number, a: number, b: number): number {
  if (v <= a) return 0;
  if (v >= b) return 1;
  return (v - a) / (b - a);
}

export function SeatMap({ data, selectedIds, stateContext, onSelect }: Props) {
  // Stage sits at the BOTTOM: mirror every seat vertically so the front row
  // (smallest posY, nearest the stage) renders lowest on screen. Section Y
  // offsets in SECTION_LAYOUT are likewise negated. Text stays upright because
  // we only negate coordinate values, never apply a scale transform.
  const flippedByLevel = useMemo(() => {
    const flip = (s: SeatDto): SeatDto => ({ ...s, posY: -s.posY });
    return {
      ground: data.seatsByLevel.ground.map(flip),
      side_balcony_left: data.seatsByLevel.side_balcony_left.map(flip),
      side_balcony_right: data.seatsByLevel.side_balcony_right.map(flip),
      upper_balcony: data.seatsByLevel.upper_balcony.map(flip),
    } as Record<Level, SeatDto[]>;
  }, [data]);
  const viewBox = useMemo(() => computeViewBox(flippedByLevel), [flippedByLevel]);
  const totalSeats = data.allSeats.length;
  const panRef = useRef<PanZoomHandle>(null);

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-4 px-2">
        <div className="kicker">
          <span className="mono text-[12px] tracking-wider text-text">{totalSeats}</span>
          <span className="ml-2">koltuk</span>
          <span className="mx-3 text-text-faint">|</span>
          <span>Bölgeye tıkla · sürükle · tekerlek</span>
        </div>
        <Legend />
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-bg-elevated shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]">
        <PanZoom ref={panRef} viewBox={viewBox}>
          {(transform) => {
            const overviewOpacity = 1 - lerp01(transform.scale, OVERVIEW_FULL_AT, OVERVIEW_HIDE_AT);
            const seatsOpacity = lerp01(transform.scale, SEATS_HIDDEN_BELOW, SEATS_FULL_AT);
            return (
              <>
                <Stage />

                {LEVEL_ORDER.map((level) => {
                  const sec = SECTION_LAYOUT[level];
                  const seats = flippedByLevel[level];
                  if (seats.length === 0) return null;

                  // Bounds of the actual seat content within section-local coords.
                  const local = sectionBounds(seats);
                  // Master-coord bbox used for zoom-to (zooms into the actual seats).
                  const masterDetailBox: Bounds = {
                    x: local.minX + sec.x,
                    y: local.minY + sec.y,
                    w: local.maxX - local.minX,
                    h: local.maxY - local.minY,
                  };

                  // Overview card centered on the section's seat-content center
                  // (in section-local coords). Independent of the seat extent.
                  const contentCenterY = (local.minY + local.maxY) / 2;
                  const overviewRect = {
                    x: -sec.overviewW / 2,
                    y: contentCenterY - sec.overviewH / 2,
                    w: sec.overviewW,
                    h: sec.overviewH,
                  };

                  return (
                    <g key={level} transform={`translate(${sec.x} ${sec.y})`}>
                      {/* Detail layer: real seats with the section frame outline */}
                      <g
                        style={{
                          opacity: seatsOpacity,
                          transition: 'opacity 180ms ease',
                          pointerEvents: seatsOpacity > 0.5 ? 'auto' : 'none',
                        }}
                      >
                        <SectionFrame bounds={local} />
                        <SeatLayer
                          seats={seats}
                          stateCtx={stateContext ?? {}}
                          selectedIds={selectedIds}
                          onSeatClick={onSelect}
                          seatSize={12}
                        />
                      </g>

                      {/* Overview card — fixed visual size, click to zoom-in */}
                      <OverviewBlock
                        rect={overviewRect}
                        title={sec.title}
                        subtitle={sec.subtitle}
                        totalSeats={seats.length}
                        opacity={overviewOpacity}
                        onClick={() => panRef.current?.zoomToBox(masterDetailBox, 0.85)}
                      />
                    </g>
                  );
                })}
              </>
            );
          }}
        </PanZoom>
      </div>
    </div>
  );
}

type OverviewProps = {
  rect: { x: number; y: number; w: number; h: number };
  title: string;
  subtitle: string;
  totalSeats: number;
  opacity: number;
  onClick: () => void;
};

function OverviewBlock({ rect, title, subtitle, totalSeats, opacity, onClick }: OverviewProps) {
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;

  // Type sizes scale relative to the card's shorter axis so all four sections
  // read with comparable weight, despite different aspect ratios.
  const titleSize = Math.min(rect.w * 0.08, rect.h * 0.18, 44);
  const captionSize = Math.max(9, titleSize * 0.26);

  return (
    <g
      onClick={(e) => {
        if (opacity <= 0.1) return;
        e.stopPropagation();
        onClick();
      }}
      style={{
        opacity,
        transition: 'opacity 180ms ease',
        pointerEvents: opacity > 0.1 ? 'auto' : 'none',
        cursor: 'pointer',
      }}
      className="overview-block"
    >
      <defs>
        <linearGradient id={`grad-${title}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.2} />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity={0.05} />
        </linearGradient>
      </defs>

      {/* Halo behind block (focus ring on hover) */}
      <rect
        x={rect.x - 6}
        y={rect.y - 6}
        width={rect.w + 12}
        height={rect.h + 12}
        rx={16}
        fill="transparent"
        stroke="var(--accent)"
        strokeOpacity={0}
        strokeWidth={1.5}
        className="overview-halo"
      />

      <rect
        x={rect.x}
        y={rect.y}
        width={rect.w}
        height={rect.h}
        rx={12}
        fill={`url(#grad-${title})`}
        stroke="var(--border-vivid)"
        strokeWidth={1}
        className="overview-fill"
      />

      <text
        x={cx}
        y={cy - captionSize * 0.5}
        textAnchor="middle"
        fontSize={titleSize}
        fill="var(--text)"
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 300,
          letterSpacing: '-0.015em',
        }}
      >
        {title}
      </text>

      <text
        x={cx}
        y={cy + captionSize * 1.4}
        textAnchor="middle"
        fontSize={captionSize}
        fill="var(--text-muted)"
        style={{ letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 500 }}
      >
        {subtitle} · {totalSeats} koltuk
      </text>

      <text
        x={cx}
        y={rect.y + rect.h - captionSize * 0.7}
        textAnchor="middle"
        fontSize={captionSize * 0.85}
        fill="var(--accent)"
        style={{ letterSpacing: '0.32em', textTransform: 'uppercase', fontWeight: 600 }}
        className="overview-cta"
      >
        Yakınlaş →
      </text>
    </g>
  );
}

function SectionFrame({
  bounds,
}: {
  bounds: { minX: number; maxX: number; minY: number; maxY: number };
}) {
  const pad = 18;
  return (
    <g aria-hidden>
      <rect
        x={bounds.minX - pad - 10}
        y={bounds.minY - 22}
        width={bounds.maxX - bounds.minX + (pad + 10) * 2}
        height={bounds.maxY - bounds.minY + 36}
        rx={8}
        fill="rgba(255,255,255,0.012)"
        stroke="var(--border)"
        strokeWidth={0.6}
      />
      <rect
        x={bounds.minX - pad - 10}
        y={bounds.minY - 8}
        width={2.5}
        height={bounds.maxY - bounds.minY + 16}
        fill="var(--accent)"
        opacity={0.35}
        rx={1}
      />
    </g>
  );
}

// Stage at the BOTTOM, just below the ground's front row (which sits at y≈0).
// Rendered as a box like the zone cards, but DELIBERATELY distinct: an accent
// (not gray) border + faint accent wash, a curved "apron" line evoking the
// stage front, and no "Yakınlaş" CTA — it holds no seats and isn't clickable.
function Stage() {
  const half = 330;
  const top = 88;
  const h = 64;
  const cy = top + h / 2;
  return (
    <g aria-hidden>
      <defs>
        <linearGradient id="stage-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.16} />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity={0.035} />
        </linearGradient>
      </defs>
      <rect
        x={-half}
        y={top}
        width={half * 2}
        height={h}
        rx={10}
        fill="url(#stage-grad)"
        stroke="var(--accent)"
        strokeWidth={1.1}
        strokeOpacity={0.5}
      />
      {/* Stage-front apron — the curved lip that reads as a stage edge. */}
      <path
        d={`M ${-half + 26} ${top + 12} Q 0 ${top + 22} ${half - 26} ${top + 12}`}
        fill="none"
        stroke="var(--accent)"
        strokeWidth={1.2}
        strokeLinecap="round"
        opacity={0.55}
      />
      <text
        x={0}
        y={cy + 13}
        textAnchor="middle"
        fontSize={25}
        fill="var(--accent)"
        style={{
          fontFamily: 'var(--font-display)',
          fontStyle: 'italic',
          fontWeight: 400,
          letterSpacing: '0.06em',
        }}
      >
        Sahne
      </text>
    </g>
  );
}

function sectionBounds(seats: SeatDto[]) {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const s of seats) {
    if (s.posX < minX) minX = s.posX;
    if (s.posY < minY) minY = s.posY;
    if (s.posX > maxX) maxX = s.posX;
    if (s.posY > maxY) maxY = s.posY;
  }
  return { minX, minY, maxX, maxY };
}

function computeViewBox(seatsByLevel: Record<Level, SeatDto[]>) {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const level of LEVEL_ORDER) {
    const seats = seatsByLevel[level];
    if (seats.length === 0) continue;
    const sec = SECTION_LAYOUT[level];
    const local = sectionBounds(seats);
    const contentCenterY = (local.minY + local.maxY) / 2;

    // Include the OVERVIEW rect since it can extend further than the seat
    // content (small balconies render large overview cards).
    const overview = {
      minX: sec.x - sec.overviewW / 2,
      maxX: sec.x + sec.overviewW / 2,
      minY: sec.y + contentCenterY - sec.overviewH / 2,
      maxY: sec.y + contentCenterY + sec.overviewH / 2,
    };
    const detail = {
      minX: sec.x + local.minX,
      maxX: sec.x + local.maxX,
      minY: sec.y + local.minY,
      maxY: sec.y + local.maxY,
    };
    minX = Math.min(minX, overview.minX, detail.minX);
    maxX = Math.max(maxX, overview.maxX, detail.maxX);
    minY = Math.min(minY, overview.minY, detail.minY);
    maxY = Math.max(maxY, overview.maxY, detail.maxY);
  }

  const padX = 40;
  const padTop = 60;
  const padBottom = 160;
  return {
    x: minX - padX,
    y: minY - padTop,
    w: maxX - minX + padX * 2,
    h: maxY - minY + padTop + padBottom,
  };
}
